import { useMemo } from "react";
import { shortRange } from "../lib/weekLine";
import { dayKeyOf, shiftDayKey } from "../lib/days";
import { useCoverageWeek } from "./useCoverageWeek";
import { type MatrixInput } from "../lib/coverage";
import { goalsDeferralNote } from "../lib/deferrals";
import { MINIMUM_WEIGHTED_SETS } from "../lib/coverageVocabulary";
import { REQUIRED_CELLS, REQUIRED_CELLS_WITHOUT_A_REASON } from "../lib/goalRequirements";
import {
  cellLabel, fillers, topFillers, gapAction, gapStateLabel, gaps, groupGoalAnswers, recognisedGoals,
  primeVerdictNote,
  requirementReadings, resolveStoredGoals, sortGaps, standardFor, summariseGaps, weekIsEmpty,
  type GapState, type RequirementReading,
} from "../lib/goalGaps";

/* 🔴 HIS §6.2, ON A SCREEN — *"the feature nothing else has"*.
 *
 * *"When an athlete adopts a goal, the engine diffs required cells against actual coverage and
 * reports the gap before a single session is generated. That is the difference between 'train
 * hard and hope' and 'here is the specific range you are not training, and here is what fills
 * it.' This single check would have saved the athlete this program was built for six months of
 * front lever holds that could never have produced the touch."*
 *
 * Step 4 shipped that diff EXECUTED and INVISIBLE: `JointActionGoalRequirements` had a gate with
 * ten rulers and nothing rendered a word of it. This is the reader.
 *
 * ── WHAT THIS SCREEN REFUSES TO FLATTEN ──────────────────────────────────────────────────
 *
 * Four pairs, and each of them is a way a gap report can look tidier than the truth:
 *
 *  1. **A ZERO and an UNKNOWN.** While the week holds an exercise nobody has described, anything
 *     short of covered is unproven — and a screen of confident holes looks exactly like a working
 *     audit. `unknown` is carried up from `provisional` and is never reported as a hole.
 *  2. **A goal that declares nothing and a goal nobody has described.** Four kinds, grouped, each
 *     with its own sentence; and an unrecognised stored string is REPORTED rather than dropped.
 *  3. **A cell nothing trains and a cell the athlete SUSPENDED.** `blockedByDeferral` is a
 *     conflict for a person: reported met it hides the conflict behind a measurement, reported
 *     zero it deletes the athlete's own statement.
 *  4. **An empty week and a week that misses everything.** Measured on the coach's own roster —
 *     one of his two clients has no assignment in the window at all — and without the sentence
 *     the report reads as an indictment of a programme rather than of its absence.
 *
 * ⚠️ READ-ONLY AND STRUCTURALLY SO. No control here writes anything, and nothing in the generator
 * reads the rule behind it — his §8 puts that at step 5 and warns against attempting 1–5 in one
 * pass. `joint-action-1`'s ruler 8 asserts the second half over every engine source.
 */

export function ClientGoals({
  clientId, today, goals, usesFullOptimization,
}: {
  clientId: string;
  today: Date;
  /** `profiles.goals`, or null when there is no profile row to read. Different things. */
  goals: readonly string[] | null;
  usesFullOptimization: boolean;
}) {
  // The same seven-day window as the coverage matrix and the standing card — `client.ts`'s
  // convention, reused rather than restated, so no two panels of one page disagree about a week.
  const from = shiftDayKey(today, -6);
  const to = dayKeyOf(today);
  const week = useCoverageWeek(clientId, from, to);

  const standard = standardFor(usesFullOptimization);

  const view = useMemo(() => {
    if (week.status !== "ready") return null;
    const answers = resolveStoredGoals(goals ?? []);
    const live = recognisedGoals(answers);
    const { readings, unresolved } = requirementReadings(live, standard, week.input);
    return {
      answers,
      groups: groupGoalAnswers(answers),
      readings,
      unresolved,
      sorted: sortGaps(gaps(readings)),
      met: readings.filter((r) => r.state === "met"),
      sum: summariseGaps(readings),
      empty: weekIsEmpty(week.input),
    };
  }, [week, goals, standard]);

  if (week.status === "loading") {
    return <p className="ht-callout loading-line" aria-live="polite">Reading their goals…</p>;
  }
  if (week.status === "error") {
    return (
      <div className="client-error" role="alert">
        <p className="ht-body">Could not read this client&rsquo;s goals against their week.</p>
        <p className="ht-callout">{week.message}</p>
      </div>
    );
  }
  if (view === null) return null;
  const { groups, sorted, met, sum, unresolved, empty } = view;

  /* A goal that requires a cell the athlete has suspended reads `blockedByDeferral` above — the
     most important row this report can produce, and it was invisible until 2026-08-31 for want of
     somewhere to keep the declaration. The SENTENCE is built in `deferrals.ts` and executed by
     `deferrals.test.ts`; a string assembled in a component has no runner. */
  const deferralNote = week.status === "ready"
    ? goalsDeferralNote(week.deferrals, dayKeyOf(today))
    : "";

  /* ⚠️ NO PROFILE AND NO GOALS ARE DIFFERENT ANSWERS, AND NEITHER IS "no requirements". A roster
     row may have no profile at all — `coach_clients` predates the account — and an athlete who
     stated nothing is a different person from one this app cannot read. */
  const goalState: "unreadable" | "none" | "some" =
    goals === null ? "unreadable" : goals.length === 0 ? "none" : "some";

  // Grouped by WHAT TO DO, which is what makes the ranking a ranking rather than a score.
  const byState: { state: GapState; rows: RequirementReading[] }[] = [];
  for (const r of sorted) {
    const last = byState[byState.length - 1];
    if (last !== undefined && last.state === r.state) last.rows.push(r);
    else byState.push({ state: r.state, rows: [r] });
  }

  return (
    <section className="goalgaps" aria-labelledby="goalgaps-h">
      <h2 id="goalgaps-h" className="ht-headline">
        What their goals require, and what is missing
      </h2>

      <p className="ht-callout goalgaps-intro">
        Every goal declares the joint actions and ranges it is made of. This is that list, checked
        against the last seven days of assigned work. A cell counts role-weighted sets —
        a prime mover earns a whole set, a synergist half, a stabiliser a
        quarter — and {MINIMUM_WEIGHTED_SETS} a week is the
        minimum, <strong>which is a guess</strong> rather than something any source states.
      </p>

      {/* 🔴 THE STANDARD IS READ, NOT PICKED. §10.2: it follows who is holding the wheel, and the
          athlete's own answer to "coach chooses for me" is what the database records. */}
      <p className="ht-callout goalgaps-standard">
        <strong>
          {standard === "fullOptimization"
            ? "Complete-athlete standard."
            : "Athlete-chosen goals."}
        </strong>{" "}
        {standard === "fullOptimization"
          ? `They asked the coach to choose everything, so every range their library could fill and
             nothing does counts as a gap the programme owes them — not only the ranges their goals
             name.`
          : `They chose their own goals, so what is required is those goals' own ranges plus a
             five-cell joint-health floor. Every other empty range is visible on the Coverage tab
             and permitted here.`}
      </p>

      {/* ⚠️ THE WINDOW LEADS, because an empty week and a week that misses everything produce the
          same states and are not the same finding. */}
      <p className="ht-callout goalgaps-window">
        {empty ? (
          <>
            <strong>Nothing is assigned in {shortRange(from, to)}.</strong> Every range below reads
            as untrained because there is no week to train it, which is a fact about the schedule
            rather than about the programme.
          </>
        ) : (
          <>
            <strong>Measured {shortRange(from, to)}.</strong>
            {/* ⚠️ ONE LEAD SENTENCE, THEN A DISCLOSURE. The caveat below is honest and a coach
                needs it ONCE — printed above the data every visit it is three lines of prose
                between them and the thing they came to read, which is the "no extra bs" his own
                rule names. Closed by default, and it is a real <details>, so it is findable,
                keyboard-operable and searchable by the browser's own find. */}
            {sum.unknown > 0
              ? ` ${sum.unknown} required range cannot be judged yet.`
              : " Every exercise in the week is described, so an empty range here is a real gap."}
            {week.hiddenSkipped > 0 && ` ${week.hiddenSkipped} hidden assignment(s) excluded.`}
            {sum.unknown > 0 && (
              <details className="ht-disclosure">
                <summary className="ht-caption">How this is counted</summary>
                <p className="ht-caption">
                  The week holds exercises nobody has described, and an undescribed exercise and
                  one that trains nothing look identical — so neither is called a gap.
                </p>
              </details>
            )}
          </>
        )}
      </p>

      <h3 className="ht-overline goalgaps-heading">Their goals</h3>
      {goalState === "unreadable" && (
        <p className="ht-callout goalgaps-empty">
          This client has no profile row, so nothing states what they are training for. That is
          not the same as having no goals — it is this app being unable to read them.
        </p>
      )}
      {goalState === "none" && (
        <p className="ht-callout goalgaps-empty">
          They have stated no goals. The joint-health floor below is required of everybody, so the
          report is not empty — but nothing here is aimed at anything they asked for.
        </p>
      )}
      {goalState === "some" && (
        <ul className="goalgaps-goals">
          {groups.map((g) => (
            <li key={g.kind + g.why} className="goalgaps-goal">
              <p className="ht-body goalgaps-goal-names">{g.goals.join(" · ")}</p>
              <p className="ht-caption goalgaps-goal-kind">{g.headline}</p>
              {g.why !== "" && <p className="ht-caption goalgaps-why">{g.why}</p>}
            </li>
          ))}
        </ul>
      )}

      <ul className="goalgaps-counts ht-caption">
        <li><strong>{sum.required}</strong> ranges required</li>
        <li><strong>{sum.met}</strong> covered</li>
        <li><strong>{sum.zero}</strong> nothing trains</li>
        <li><strong>{sum.thin}</strong> thin</li>
        {sum.blockedByDeferral > 0 && (
          <li><strong>{sum.blockedByDeferral}</strong> deferred and needed</li>
        )}
        {sum.metAmbiguous > 0 && <li><strong>{sum.metAmbiguous}</strong> covered, by what is unclear</li>}
        {sum.unknown > 0 && <li><strong>{sum.unknown}</strong> not yet known</li>}
        <li><strong>{sum.fillable}</strong> their own library could fill</li>
        {/* 🔴 …AND BY WHAT. `fillable` counts cells the library MENTIONS; a cell it mentions only
            as a stabiliser earns a quarter of a set, so filling it from what exists is eight sets
            where a driving movement needs two. Measured, `spine.anti_rotation · mid` — required of
            every athlete — carries 39 rows and no prime mover, so the count above was overstating
            on a joint-health floor cell for everybody. */}
        <li><strong>{sum.fillableByPrimeMover}</strong> of those with a prime mover</li>
      </ul>

      {unresolved.length > 0 && (
        <p className="ht-callout goalgaps-unresolved" role="alert">
          {unresolved.length} required range names a joint action this app&rsquo;s vocabulary does
          not have ({unresolved.join(", ")}). Both tables are generated from one source, so this
          means one of them was regenerated and the other was not.
        </p>
      )}

      {sorted.length === 0 ? (
        <p className="ht-callout goalgaps-clear">
          Every range their goals require is covered.
        </p>
      ) : (
        byState.map((group) => (
          <div key={group.state} className="goalgaps-group">
            <h3 className={`ht-overline goalgaps-heading goalgaps-h-${group.state}`}>
              {gapStateLabel(group.state)} · {group.rows.length}
            </h3>
            {/* The action is what justifies the order being an order: each state names a
                different thing to do, so a single score would blend them. */}
            <p className="ht-caption goalgaps-action">{gapAction(group.state)}</p>
            <ul className="goalgaps-rows">
              {group.rows.map((r) => (
                <Row key={cellLabel(r.action, r.required.zone)} r={r}
                     names={week.libraryNames} rows={week.input.rows} />
              ))}
            </ul>
          </div>
        ))
      )}

      {met.length > 0 && (
        /* Behind a disclosure: the screen answers ONE question — what is missing — and the covered
           ranges are the evidence that the missing ones are a finding rather than a broken read.
           §2b's progressive disclosure, never feature hiding. */
        <details className="goalgaps-met">
          <summary className="ht-caption">
            {met.length} required range{met.length === 1 ? " is" : "s are"} covered
          </summary>
          <ul className="goalgaps-rows">
            {met.map((r) => (
              <li key={cellLabel(r.action, r.required.zone)} className="goalgaps-row">
                <p className="ht-body goalgaps-cell">{cellLabel(r.action, r.required.zone)}</p>
                <p className="ht-caption">
                  {fmt(r.reading.weightedSets)} weighted sets · asked by {r.askedBy.join(", ")}
                </p>
              </li>
            ))}
          </ul>
        </details>
      )}

      <p className="ht-caption goalgaps-note">
        {deferralNote}
        {" "}
        {REQUIRED_CELLS_WITHOUT_A_REASON} of the {REQUIRED_CELLS} declared requirements carry no
        stated reason yet, so some rows above say what is needed without saying why.
      </p>
    </section>
  );
}

function Row({
  r, names, rows,
}: {
  r: RequirementReading;
  names: ReadonlyMap<string, string>;
  rows: MatrixInput["rows"];
}) {
  // ⚠️ CAPPED, AND ONLY RENDERING SHOWED WHY: against his real library one gap named more than a
  // dozen exercises on a single row, and five gaps became a wall of names — §2b's "extra bs" and
  // the wall-of-warnings failure mode, arriving in the FIX rather than in the diagnosis. The
  // remainder is COUNTED rather than dropped, because "three of eleven" and "three" differ.
  const fill = topFillers(fillers(r.required.action, r.required.zone, rows, names));
  return (
    <li className={`goalgaps-row goalgaps-${r.state}`}>
      <p className="ht-body goalgaps-cell">
        {cellLabel(r.action, r.required.zone)}
        {/* `defining` versus `supporting` — a fact about the movement, never a dose. It is what
            lets the report say "you cannot do this" rather than "this will limit you". */}
        <span className="ht-caption goalgaps-emphasis">
          {r.required.emphasis === "defining" ? " · defining" : " · supporting"}
        </span>
      </p>
      <p className="ht-caption goalgaps-measure">
        {r.reading.weightedSets === 0
          ? "Nothing this week"
          : `${fmt(r.reading.weightedSets)} weighted sets, ${fmt(r.reading.primeWeighted)} of them prime`}
        {" · asked by "}{r.askedBy.join(", ")}
      </p>
      {/* ⚠️ OMITTED WHEN EMPTY RATHER THAN RENDERED BLANK. 26 of the 112 declarations carry no
          sentence, and an empty quotation mark reads as a broken screen. */}
      {r.required.why !== "" && <p className="ht-caption goalgaps-why">{r.required.why}</p>}
      {r.ambiguity !== null && <p className="ht-caption goalgaps-why">{r.ambiguity}</p>}
      {r.reading.deferral !== null && (
        <p className="ht-caption goalgaps-why">
          Deferred: {r.reading.deferral.reason} (review {r.reading.deferral.reviewOn}).
        </p>
      )}
      {/* 🔴 THE SECOND HALF OF HIS SENTENCE — *"and here is what fills it"*. The role is shown
          because a stabiliser earns a quarter of a set: "this fills it" means eight sets for a
          bracing row and two for a driving one. */}
      {fill.shown.length > 0 && (
        <p className="ht-caption goalgaps-fillers">
          {`In their library: ${fill.shown.map((f) => `${f.name} (${f.role})`).join(", ")}`
            + (fill.more > 0 ? ` — and ${fill.more} more` : "")}
        </p>
      )}
      {/* 🔴 AND WHETHER ANY OF THEM DRIVES IT — §14.13's verdict, in a sentence the tests own.
          ⚠️ THIS REPLACED A LINE THAT OVERSTATED. The empty-filler branch used to read *"a gap in
          the library rather than in this athlete"*, which is the CONTENT claim §14.13 says the
          `undescribed` answer may not carry: no row mentioning a cell means nobody wrote one down,
          not that no movement exists. The verdict separates that from the real content gap, where
          the library describes the cell 39 times and drives it never. */}
      {primeVerdictNote(r.libraryPrimeVerdict) !== null && (
        <p className="ht-caption goalgaps-fillers">{primeVerdictNote(r.libraryPrimeVerdict)}</p>
      )}
    </li>
  );
}

function fmt(n: number): string {
  return n === 0 ? "0" : Number.isInteger(n) ? String(n) : n.toFixed(2);
}
